import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  FilterList as FilterIcon,
  LockOutlined as LockIcon,
  Menu as MenuIcon,
  VisibilityOutlined as RevealIcon,
} from '@mui/icons-material';
import type { PricePromoUnit } from '../../services/api';
import PricePromoPriceButton from './PricePromoPriceButton';

type SortDirection = 'default' | 'asc' | 'desc';
type ColumnType = 'text' | 'number' | 'bedroom';
type ColumnKey =
  | 'projectName'
  | 'unitNumber'
  | 'blockNumber'
  | 'stackNumber'
  | 'bedroomNumber'
  | 'size'
  | 'price'
  | 'pricePSF'
  | 'priceChange'
  | 'priceChangePercent';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  type: ColumnType;
  align: 'left' | 'right' | 'center';
  minWidth: number;
  /** First column stays pinned while the table scrolls horizontally. */
  sticky?: boolean;
}

// Every PricePromoUnit attribute, each with a sortable + filterable header.
const COLUMNS: ColumnDef[] = [
  { key: 'projectName', label: 'Project', type: 'text', align: 'left', minWidth: 180, sticky: true },
  { key: 'unitNumber', label: 'Unit', type: 'text', align: 'left', minWidth: 120 },
  { key: 'blockNumber', label: 'Block', type: 'text', align: 'center', minWidth: 80 },
  { key: 'stackNumber', label: 'Stack', type: 'text', align: 'center', minWidth: 70 },
  { key: 'bedroomNumber', label: 'Bed', type: 'bedroom', align: 'center', minWidth: 64 },
  { key: 'size', label: 'Size (sqft)', type: 'number', align: 'right', minWidth: 100 },
  { key: 'price', label: 'Price', type: 'number', align: 'right', minWidth: 120 },
  { key: 'pricePSF', label: 'PSF', type: 'number', align: 'right', minWidth: 90 },
  { key: 'priceChange', label: 'Price Promo', type: 'number', align: 'right', minWidth: 120 },
  { key: 'priceChangePercent', label: 'Change %', type: 'number', align: 'right', minWidth: 90 },
];

/** Default sort: biggest price promo (largest drop) first. */
const DEFAULT_SORT: { column: ColumnKey | null; direction: SortDirection } = {
  column: 'priceChange',
  direction: 'desc',
};

/**
 * Columns hidden from non-admin users until they reveal a row. Everything
 * except Project, Price and Price Promo is masked, so agents can see that a
 * unit is on promo (and by how much) but not pinpoint which specific unit
 * without spending one of their daily reveals.
 */
const MASKED_COLUMN_KEYS: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  'unitNumber',
  'blockNumber',
  'stackNumber',
  'bedroomNumber',
  'size',
  'pricePSF',
  'priceChangePercent',
]);

/** Stack number from an explicit field, else parsed from `#floor-stack`. */
function stackOf(u: PricePromoUnit): string {
  if (u.stackNumber && u.stackNumber.trim()) return u.stackNumber.trim();
  const m = String(u.UnitNumber).match(/#?\d+-(\d+)/);
  return m ? m[1] : '';
}

function toNum(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

/** Sentinel bedroom-filter value for penthouses (matches Super Picker's tower view). */
const PENTHOUSE_FILTER_VALUE = -1;

/** Same penthouse rule as Super Picker's tower view: unit type mentions PH/penthouse. */
function isPenthouseText(value: unknown): boolean {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return false;
  return raw.includes('penthouse') || raw.includes('penthhouse') || /\bph\b/.test(raw) || raw.endsWith('ph');
}

function isPenthouseUnit(u: PricePromoUnit): boolean {
  return isPenthouseText(u.unitTypeDescription) || isPenthouseText(u.unitType);
}

/** Comparable value for a column — string for text, number otherwise. */
function rawValue(u: PricePromoUnit, key: ColumnKey): string | number {
  switch (key) {
    case 'projectName': return u.projectName ?? '';
    case 'unitNumber': return u.UnitNumber ?? '';
    case 'blockNumber': return (u.blockNumber ?? '').toString();
    case 'stackNumber': return stackOf(u);
    case 'bedroomNumber': return typeof u.bedroomNumber === 'number' ? u.bedroomNumber : NaN;
    case 'size': return toNum(u.size);
    case 'price': return typeof u.price === 'number' ? u.price : NaN;
    case 'pricePSF': return typeof u.pricePSF === 'number' ? u.pricePSF : NaN;
    // Promo shown as a positive amount, so sort/filter on its magnitude.
    case 'priceChange': return typeof u.priceChange === 'number' ? Math.abs(u.priceChange) : NaN;
    case 'priceChangePercent': return typeof u.priceChangePercent === 'number' ? u.priceChangePercent : NaN;
    default: return '';
  }
}

export interface PricePromoTableViewProps {
  /** Units to display (already scoped by the dialog's search + bedroom chips). */
  units: PricePromoUnit[];
  formatCurrency: (value: number) => string;
  formatSqft: (value: number | string | null | undefined) => string;
  /** Row click — mirrors the tab view's card click (navigates to the unit). */
  onUnitClick: (unit: PricePromoUnit) => void;
  /** Price click — mirrors the tab view (opens the price-history chart). */
  onPriceClick: (unit: PricePromoUnit) => void;
  /** Admins (and equivalents) see every cell unmasked with no reveal limit. */
  isAdmin?: boolean;
  /** Ids of units the current user has already revealed today (stay revealed). */
  revealedUnitIds?: ReadonlySet<number>;
  /** Reveals left in today's budget (non-admin only). */
  revealsRemaining?: number;
  /** Daily reveal cap, for the "X of Y" badge. */
  revealLimit?: number;
  /** True once the reveal budget has loaded from the server. */
  revealsLoaded?: boolean;
  /**
   * Reveal a masked row's hidden cells (non-admin). The parent records the
   * reveal server-side, enforces the daily cap and updates `revealedUnitIds`.
   */
  onRevealUnit?: (unit: PricePromoUnit) => void;
}

const EMPTY_REVEALED: ReadonlySet<number> = new Set<number>();

/**
 * Landscape table view for the Price Promo dialog: every unit as a flat row,
 * with sort (asc/desc) and type-aware filter popovers on each column header.
 */
const PricePromoTableView: React.FC<PricePromoTableViewProps> = ({
  units,
  formatCurrency,
  formatSqft,
  onUnitClick,
  onPriceClick,
  isAdmin = false,
  revealedUnitIds = EMPTY_REVEALED,
  revealsRemaining = 0,
  revealLimit = 0,
  revealsLoaded = false,
  onRevealUnit,
}) => {
  const [sort, setSort] = useState<{ column: ColumnKey | null; direction: SortDirection }>({ ...DEFAULT_SORT });
  const [textFilters, setTextFilters] = useState<Partial<Record<ColumnKey, string>>>({});
  const [rangeFilters, setRangeFilters] = useState<Partial<Record<ColumnKey, { min: string; max: string }>>>({});
  const [bedroomFilter, setBedroomFilter] = useState<number[]>([]);
  const [popover, setPopover] = useState<{ element: HTMLElement; column: ColumnKey } | null>(null);
  const bedroomOptions = useMemo(() => {
    const set = new Set<number>();
    let hasPenthouse = false;
    units.forEach((u) => {
      if (isPenthouseUnit(u)) {
        hasPenthouse = true;
        return;
      }
      if (typeof u.bedroomNumber === 'number' && u.bedroomNumber > 0) set.add(u.bedroomNumber);
    });
    return [...Array.from(set).sort((a, b) => a - b), ...(hasPenthouse ? [PENTHOUSE_FILTER_VALUE] : [])];
  }, [units]);

  // ── Masking (non-admin only) ────────────────────────────────────────────
  // Admins see every cell. For everyone else, the columns in MASKED_COLUMN_KEYS
  // are hidden behind a dotted placeholder until the row is revealed. Reveals
  // are capped per day and enforced server-side by the parent.
  //
  // Admins can flip on a "non-admin view" preview that simulates the masked
  // experience entirely locally: reveals go into previewRevealedIds and never
  // touch the server, so no real reveal quota is spent or recorded.
  const [previewAsNonAdmin, setPreviewAsNonAdmin] = useState(false);
  const [previewRevealedIds, setPreviewRevealedIds] = useState<ReadonlySet<number>>(new Set<number>());
  const previewActive = isAdmin && previewAsNonAdmin;
  /** Mirrors the backend DAILY_REVEAL_LIMIT so the preview badge counts down like the real one. */
  const PREVIEW_REVEAL_LIMIT = 10;
  const effectiveRevealedIds = previewActive ? previewRevealedIds : revealedUnitIds;
  const effectiveRevealsLoaded = previewActive || revealsLoaded;
  const effectiveRevealLimit = previewActive ? PREVIEW_REVEAL_LIMIT : revealLimit;
  const effectiveRevealsRemaining = previewActive
    ? Math.max(0, PREVIEW_REVEAL_LIMIT - previewRevealedIds.size)
    : revealsRemaining;

  const maskingEnabled = !isAdmin || previewActive;
  const isRowRevealed = (u: PricePromoUnit): boolean => !maskingEnabled || effectiveRevealedIds.has(u.id);
  const isRowMasked = (u: PricePromoUnit): boolean => maskingEnabled && !isRowRevealed(u);
  /** A column whose values are hidden for this user (so its sort/filter is locked too). */
  const isColumnMaskable = (key: ColumnKey): boolean => maskingEnabled && MASKED_COLUMN_KEYS.has(key);
  const atRevealCap = maskingEnabled && effectiveRevealsLoaded && effectiveRevealsRemaining <= 0;

  const handleRowActivate = (u: PricePromoUnit) => {
    if (!isRowMasked(u)) {
      onUnitClick(u);
      return;
    }
    // Masked row: first tap reveals it (a second tap then navigates).
    if (atRevealCap) return; // cap reached — the header badge explains why
    if (previewActive) {
      setPreviewRevealedIds((prev) => new Set(prev).add(u.id));
      return;
    }
    onRevealUnit?.(u);
  };

  const sortDirOf = (column: ColumnKey): SortDirection =>
    sort.column === column ? sort.direction : 'default';

  const hasActiveFilter = (col: ColumnDef): boolean => {
    if (col.type === 'text') return (textFilters[col.key] ?? '').trim() !== '';
    if (col.type === 'number') {
      const r = rangeFilters[col.key];
      return !!r && ((r.min ?? '').trim() !== '' || (r.max ?? '').trim() !== '');
    }
    if (col.type === 'bedroom') return bedroomFilter.length > 0;
    return false;
  };

  const isDefaultSort = sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction;
  const anyFilterActive = COLUMNS.some(hasActiveFilter) || !isDefaultSort;

  const rows = useMemo(() => {
    const filtered = units.filter((u) => {
      for (const col of COLUMNS) {
        if (col.type === 'text') {
          const q = (textFilters[col.key] ?? '').trim().toLowerCase();
          if (q && !String(rawValue(u, col.key)).toLowerCase().includes(q)) return false;
        } else if (col.type === 'number') {
          const r = rangeFilters[col.key];
          if (r) {
            const v = rawValue(u, col.key) as number;
            const min = r.min.trim() !== '' ? Number(r.min) : NaN;
            const max = r.max.trim() !== '' ? Number(r.max) : NaN;
            if (!Number.isNaN(min) && (Number.isNaN(v) || v < min)) return false;
            if (!Number.isNaN(max) && (Number.isNaN(v) || v > max)) return false;
          }
        } else if (col.type === 'bedroom') {
          if (bedroomFilter.length) {
            const unitIsPenthouse = isPenthouseUnit(u);
            const matches = bedroomFilter.some((filterValue) => {
              if (filterValue === PENTHOUSE_FILTER_VALUE) return unitIsPenthouse;
              return !unitIsPenthouse && Number(u.bedroomNumber) === filterValue;
            });
            if (!matches) return false;
          }
        }
      }
      return true;
    });

    if (sort.column && sort.direction !== 'default') {
      const col = COLUMNS.find((c) => c.key === sort.column);
      if (col) {
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
          const va = rawValue(a, col.key);
          const vb = rawValue(b, col.key);
          if (col.type === 'text') return String(va).localeCompare(String(vb)) * dir;
          const na = typeof va === 'number' ? va : NaN;
          const nb = typeof vb === 'number' ? vb : NaN;
          // Missing values sink to the bottom regardless of direction.
          if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
          if (Number.isNaN(na)) return 1;
          if (Number.isNaN(nb)) return -1;
          return (na - nb) * dir;
        });
      }
    }
    return filtered;
  }, [units, textFilters, rangeFilters, bedroomFilter, sort]);

  const setSortDir = (column: ColumnKey, direction: SortDirection) => {
    setSort({ column, direction });
    setPopover(null);
  };

  const clearColumnFilter = (col: ColumnDef) => {
    if (col.type === 'text') {
      setTextFilters((prev) => ({ ...prev, [col.key]: '' }));
    } else if (col.type === 'number') {
      setRangeFilters((prev) => ({ ...prev, [col.key]: { min: '', max: '' } }));
    } else if (col.type === 'bedroom') {
      setBedroomFilter([]);
    }
  };

  const clearAllFilters = () => {
    setTextFilters({});
    setRangeFilters({});
    setBedroomFilter([]);
    setSort({ ...DEFAULT_SORT });
  };

  const setRange = (col: ColumnKey, type: 'min' | 'max', value: string) => {
    setRangeFilters((prev) => ({
      ...prev,
      [col]: { min: '', max: '', ...prev[col], [type]: value },
    }));
  };

  const renderSortIcon = (direction: SortDirection) => {
    if (direction === 'default') return <MenuIcon fontSize="small" />;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {direction === 'asc'
          ? <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
          : <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />}
        <MenuIcon fontSize="small" />
      </Box>
    );
  };

  const iconBtnSx = (col: ColumnDef) => {
    const active = hasActiveFilter(col) || sortDirOf(col.key) !== 'default';
    return {
      p: 0.5,
      color: active ? 'var(--color-accent)' : 'var(--color-text-strong)',
      backgroundColor: active ? 'rgba(var(--color-accent-rgb), 0.12)' : 'transparent',
      '&:hover': { backgroundColor: 'var(--color-surface-ghost-strong)', color: 'var(--color-accent)' },
    };
  };

  const justify = (align: ColumnDef['align']) =>
    align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';

  const activeColumn = popover ? COLUMNS.find((c) => c.key === popover.column) ?? null : null;

  const renderCell = (u: PricePromoUnit, col: ColumnDef) => {
    if (isColumnMaskable(col.key) && !isRowRevealed(u)) {
      return (
        <Typography
          component="span"
          variant="body2"
          aria-label="Hidden — tap the row to reveal"
          sx={{ color: 'rgba(var(--color-text-rgb), 0.35)', letterSpacing: '0.18em', userSelect: 'none' }}
        >
          ••••
        </Typography>
      );
    }
    switch (col.key) {
      case 'projectName':
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>
            {u.projectName}
          </Typography>
        );
      case 'unitNumber':
        return u.UnitNumber;
      case 'blockNumber':
        return u.blockNumber || '—';
      case 'stackNumber':
        return stackOf(u) || '—';
      case 'bedroomNumber': {
        const isPh = isPenthouseUnit(u);
        if (u.bedroomNumber) return isPh ? `${u.bedroomNumber}BR (PH)` : `${u.bedroomNumber}BR`;
        return isPh ? 'PH' : '—';
      }
      case 'size': {
        const s = toNum(u.size);
        return Number.isNaN(s) ? '—' : formatSqft(u.size);
      }
      case 'price':
        return (
          <PricePromoPriceButton
            priceLabel={typeof u.price === 'number' ? formatCurrency(u.price) : '—'}
            showChip={false}
            onClick={() => onPriceClick(u)}
          />
        );
      case 'pricePSF':
        return typeof u.pricePSF === 'number' && Number.isFinite(u.pricePSF) ? formatCurrency(u.pricePSF) : '—';
      case 'priceChange':
        return (
          <Typography component="span" variant="body2" sx={{ color: 'var(--color-success)', fontWeight: 600 }}>
            {typeof u.priceChange === 'number'
              ? formatCurrency(Math.abs(u.priceChange))
              : '—'}
          </Typography>
        );
      case 'priceChangePercent':
        return (
          <Typography component="span" variant="body2" sx={{ color: 'var(--color-success)', fontWeight: 600 }}>
            {u.priceChangePercent ? `${u.priceChangePercent.toFixed(1)}%` : '—'}
          </Typography>
        );
      default:
        return '—';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
          {rows.length} unit{rows.length === 1 ? '' : 's'}
          {rows.length !== units.length ? ` of ${units.length}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {maskingEnabled && effectiveRevealsLoaded && (
            <Chip
              size="small"
              icon={atRevealCap ? <LockIcon sx={{ fontSize: 16 }} /> : <RevealIcon sx={{ fontSize: 16 }} />}
              label={atRevealCap
                ? 'Daily reveal limit reached — resets tomorrow'
                : `${effectiveRevealsRemaining} of ${effectiveRevealLimit} reveals left today`}
              sx={{
                fontWeight: 600,
                color: atRevealCap ? 'var(--color-danger)' : 'var(--color-info)',
                bgcolor: atRevealCap
                  ? 'rgba(var(--color-danger-rgb), 0.12)'
                  : 'rgba(var(--color-info-rgb), 0.12)',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={previewAsNonAdmin}
                  onChange={(e) => {
                    setPreviewAsNonAdmin(e.target.checked);
                    // Fresh preview each time it's switched on; switching off unmasks everything.
                    setPreviewRevealedIds(new Set<number>());
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', whiteSpace: 'nowrap' }}>
                  Non-admin view
                </Typography>
              }
              title="Preview the masked table non-admin users see — reveals here are simulated and don't spend anyone's quota"
              sx={{ mr: 0, ml: 0 }}
            />
          )}
          {anyFilterActive && (
            <Button size="small" onClick={clearAllFilters} sx={{ color: 'var(--color-accent)' }}>
              Clear all
            </Button>
          )}
        </Box>
      </Box>
      {maskingEnabled && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          Unit details are hidden — tap a row to reveal it{effectiveRevealsLoaded ? '' : '.'}
          {effectiveRevealsLoaded && ` (uses 1 of your ${effectiveRevealLimit} daily reveals).`}
          {previewActive && ' Preview only — simulated reveals, nothing is recorded.'}
        </Typography>
      )}

      <TableContainer sx={{ maxHeight: '65vh', overflow: 'auto', borderRadius: 2, border: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            '& .MuiTableCell-root': {
              py: 0.75,
              px: 1,
              fontSize: '0.8rem',
              borderColor: 'rgba(var(--color-text-rgb), 0.08)',
              color: 'var(--color-text-strong)',
            },
            '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: 'var(--color-surface-thead)', fontWeight: 600 },
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{
                    fontWeight: 600,
                    color: 'var(--color-text-strong)',
                    minWidth: col.minWidth,
                    whiteSpace: 'nowrap',
                    ...(col.sticky
                      ? {
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          bgcolor: 'var(--color-surface-thead)',
                          borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)',
                        }
                      : {}),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: justify(col.align), gap: 0.5 }}>
                    {col.label}
                    {/* Masked columns get no sort/filter control — otherwise a non-admin
                        could rank the hidden values without spending any reveals. */}
                    {isColumnMaskable(col.key) ? (
                      <LockIcon sx={{ fontSize: 15, color: 'rgba(var(--color-text-rgb), 0.3)' }} />
                    ) : (
                      <IconButton size="small" onClick={(e) => setPopover({ element: e.currentTarget, column: col.key })} sx={iconBtnSx(col)}>
                        {renderSortIcon(sortDirOf(col.key))}
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 3, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                  No units match your filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u) => (
                <TableRow
                  hover
                  key={u.id}
                  onClick={() => handleRowActivate(u)}
                  title={isRowMasked(u)
                    ? (atRevealCap
                        ? 'Daily reveal limit reached — resets tomorrow'
                        : 'Tap to reveal this unit (uses 1 of your daily reveals)')
                    : undefined}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(var(--color-sheet-rgb), 0.35)' } }}
                >
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={col.sticky
                        ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            bgcolor: 'var(--color-surface)',
                            borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)',
                          }
                        : undefined}
                    >
                      {renderCell(u, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(popover)}
        anchorEl={popover?.element}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {activeColumn && (
          <Box sx={{ p: 2, minWidth: 260 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                Sort
              </Typography>
              <ToggleButtonGroup
                value={sortDirOf(activeColumn.key)}
                exclusive
                onChange={(_, value) => { if (value !== null) setSortDir(activeColumn.key, value); }}
                fullWidth
                sx={{
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                    '&:not(:first-of-type)': { borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)', marginLeft: 0 },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-info)',
                      color: 'var(--color-text-strong)',
                      '&:hover': { backgroundColor: 'var(--color-info-strong)' },
                    },
                  },
                }}
              >
                <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>Filter</Typography>
                <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
              </Box>

              {activeColumn.type === 'text' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Filter ${activeColumn.label}`}
                    value={textFilters[activeColumn.key] ?? ''}
                    onChange={(e) => setTextFilters((prev) => ({ ...prev, [activeColumn.key]: e.target.value }))}
                    InputProps={{
                      endAdornment: (textFilters[activeColumn.key] ?? '').trim() !== '' && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => clearColumnFilter(activeColumn)} sx={{ p: 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: 12 }}>✕</Typography>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {activeColumn.type === 'number' && (() => {
                const range = rangeFilters[activeColumn.key] ?? { min: '', max: '' };
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Min"
                      value={range.min}
                      onChange={(e) => setRange(activeColumn.key, 'min', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max"
                      value={range.max}
                      onChange={(e) => setRange(activeColumn.key, 'max', e.target.value)}
                    />
                    <Button
                      size="small"
                      fullWidth
                      onClick={() => clearColumnFilter(activeColumn)}
                      disabled={!hasActiveFilter(activeColumn)}
                      sx={{ mt: 0.5 }}
                    >
                      Clear Filter
                    </Button>
                  </Box>
                );
              })()}

              {activeColumn.type === 'bedroom' && (
                <Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {bedroomOptions.length === 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>No bedroom data.</Typography>
                    )}
                    {bedroomOptions.map((bed) => {
                      const active = bedroomFilter.includes(bed);
                      return (
                        <Chip
                          key={bed}
                          label={bed === PENTHOUSE_FILTER_VALUE ? 'PH' : `${bed}BR`}
                          size="small"
                          clickable
                          variant={active ? 'filled' : 'outlined'}
                          onClick={() => setBedroomFilter((prev) => prev.includes(bed) ? prev.filter((b) => b !== bed) : [...prev, bed])}
                          sx={{
                            fontWeight: 600,
                            bgcolor: active ? 'var(--color-info-navy)' : 'transparent',
                            color: active ? '#fff' : 'rgba(var(--color-text-rgb), 0.85)',
                            borderColor: 'rgba(var(--color-text-rgb), 0.25)',
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Button size="small" fullWidth onClick={() => clearColumnFilter(activeColumn)} disabled={bedroomFilter.length === 0} sx={{ mt: 1 }}>
                    Clear Filter
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Popover>

    </Box>
  );
};

export default PricePromoTableView;
